/**
 * How a visitor reached a vendor's public browse page (TRO-644).
 *
 * `table_qr_opened` is named for the table QR a customer scans standing at a booth, but it also
 * counted visitors who arrived through an Instagram or Facebook link without ever standing in front
 * of the stall. The two audiences behave differently, so a rate computed over both on the browse
 * funnel means nothing in particular.
 *
 * The values name the evidence, not the inference. `direct` means "no referrer and no click id",
 * which is what a QR scan looks like, and also what a typed, pasted, or bookmarked URL looks like.
 * Calling that bucket `table_qr` would claim a certainty the data does not carry.
 *
 * A referrer alone is not enough: an in-app browser can strip the referrer from a social visit
 * that still has an `fbclid` in its URL (`$referring_domain = $direct`), and a referrer-only split
 * files that as a QR scan. Both signals are checked, and the social one wins.
 */

/** Registrable domains whose links are social traffic. Subdomains match; lookalikes do not. */
const SOCIAL_DOMAINS = [
  "facebook.com",
  "instagram.com",
  "linkedin.com",
  "pinterest.com",
  "reddit.com",
  "snapchat.com",
  "t.co",
  "threads.net",
  "tiktok.com",
  "twitter.com",
  "x.com",
  "youtube.com",
];

/**
 * Click-tracking parameters the social networks append. Their presence is the signal; the value is
 * an opaque tracking token that is never read, sent, or stored.
 */
const SOCIAL_CLICK_IDS = ["fbclid", "igsh", "igshid", "ttclid", "twclid"];

export type BrowseArrival =
  /** A social referrer, or a social click id in the URL. */
  | "social"
  /** No referrer and no click id, which is what a scanned table QR looks like. */
  | "direct"
  /** Some other site linked here. */
  | "referral"
  /** The referrer is this same host: a re-entry within the browse surface, not an arrival. */
  | "internal";

/**
 * The host of a URL, lowercased, or "" when it has none.
 *
 * Hand-rolled rather than `new URL()`: `URLSearchParams` has been unavailable under Hermes, where
 * it threw inside a `catch` and silently stopped first-touch attribution from recording anything.
 * Parsing a referrer string is not worth a runtime API that can be missing under one JS engine.
 */
function hostOf(url: string): string {
  const withoutScheme = url.trim().toLowerCase().replace(/^[a-z][a-z0-9+.-]*:\/\//, "");
  if (withoutScheme === url.trim().toLowerCase()) return ""; // no scheme: not an absolute URL
  const host = withoutScheme.split(/[/?#]/)[0] ?? "";
  // Strip credentials and port; an IPv6 literal keeps its brackets and simply never matches.
  return host.split("@").pop()?.replace(/:\d+$/, "") ?? "";
}

/** True when `host` is `domain` or a subdomain of it, never a substring match. */
function isHostUnder(host: string, domain: string): boolean {
  return host === domain || host.endsWith(`.${domain}`);
}

/** True when the URL's query or fragment carries one of the social click-id parameters. */
function hasSocialClickId(url: string): boolean {
  const query = url.toLowerCase().slice(url.search(/[?#]/) + 1);
  if (!query || url.search(/[?#]/) < 0) return false;
  return query.split(/[&?#]/).some((pair) => {
    const name = pair.split("=")[0];
    return name !== undefined && SOCIAL_CLICK_IDS.includes(name);
  });
}

/**
 * Classify one arrival from the referrer and the landing URL.
 *
 * Order matters. A social click id wins over everything, because it is the only signal that
 * survives an in-app browser stripping the referrer. `internal` is checked before `referral` so a
 * back-navigation within the browse surface is not counted as another site sending traffic.
 */
export function browseArrival(input: { referrer: string; url: string; }): BrowseArrival {
  if (hasSocialClickId(input.url)) return "social";

  const referrerHost = hostOf(input.referrer);
  if (!referrerHost) return "direct";
  if (SOCIAL_DOMAINS.some((domain) => isHostUnder(referrerHost, domain))) return "social";

  const currentHost = hostOf(input.url);
  if (currentHost && referrerHost === currentHost) return "internal";

  return "referral";
}
